import React, { useState, useEffect, useCallback } from 'react';
import { useCart } from '../CartContext';
import { X, Check, Utensils, Pizza, Sandwich, Coffee, IceCreamCone, Salad, Soup, Beef, Cookie, CupSoda } from 'lucide-react';
import './ProductDetail.css';

function formatPrice(price) {
  return `£${Number(price).toFixed(2)}`;
}

const FOOD_ICONS = [Utensils, Pizza, Sandwich, Coffee, IceCreamCone, Salad, Soup, Beef, Cookie, CupSoda];
function PlaceholderIcon({ product, size = 48 }) {
  const idx = (product.id || 0) % FOOD_ICONS.length;
  const Icon = FOOD_ICONS[idx];
  return <Icon size={size} strokeWidth={1.5} />;
}

export default function ProductDetail({ product, preSelectedVariation, onClose, editMode, editCartItem, onEditComplete }) {
  const { addItem } = useCart();

  const variations = product.variations || [];
  const [selectedVariation, setSelectedVariation] = useState(
    preSelectedVariation || (variations.length > 0 ? variations[0] : null)
  );

  // In edit mode, re-hydrate modifiers from cart item's { groupName: [names] } format
  const [selectedModifiers, setSelectedModifiers] = useState(() => {
    if (!editMode || !editCartItem?.modifiers) return {};
    const variation = preSelectedVariation || (variations.length > 0 ? variations[0] : null);
    const modConfig = variation?.modifiers || product.modifiers || {};
    const hydrated = {};
    for (const [group, names] of Object.entries(editCartItem.modifiers)) {
      if (!Array.isArray(names)) continue;
      const serverGroup = modConfig[group] || [];
      hydrated[group] = names.map(name => {
        const match = serverGroup.find(m => (typeof m === 'object' ? m.name : m) === name);
        return match && typeof match === 'object' ? match : { name, price: 0 };
      });
    }
    return hydrated;
  });
  const [quantity, setQuantity] = useState(editMode && editCartItem ? editCartItem.quantity : 1);

  // In edit mode, separate notes from "NO: ..." and "SWAP: ..." lines
  const [notes, setNotes] = useState(() => {
    if (!editMode || !editCartItem?.notes) return '';
    return editCartItem.notes
      .split('\n')
      .filter(l => !l.match(/^NO:\s/i) && !l.match(/^SWAP:\s/i))
      .join('\n')
      .trim();
  });
  const [added, setAdded] = useState(false);
  const [visible, setVisible] = useState(false);

  // Slide-up animation on mount
  useEffect(() => {
    // Small delay so the CSS transition fires
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Reset modifiers when variation changes
  useEffect(() => {
    setSelectedModifiers({});
  }, [selectedVariation]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 260); // match transition duration
  }, [onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleClose]);

  const unitPrice = selectedVariation
    ? Number(selectedVariation.price)
    : Number(product.price);

  // Calculate modifier cost
  const modifierCost = Object.values(selectedModifiers)
    .flat()
    .reduce((sum, m) => sum + (Number(m.price) || 0), 0);

  const lineTotal = (unitPrice + modifierCost) * quantity;

  // Build modifiers from selected variation — normalize to { groupName: [items] } format
  const rawModifiers = selectedVariation?.modifiers || product.modifiers || null;
  const modifierGroups = (() => {
    if (!rawModifiers) return {};
    // Already grouped: { "Extras": [...], "Sauces": [...] }
    if (!Array.isArray(rawModifiers) && typeof rawModifiers === 'object') {
      // Check if values are arrays (grouped format)
      const firstVal = Object.values(rawModifiers)[0];
      if (Array.isArray(firstVal)) return rawModifiers;
      // Single flat object — not a group, skip
      return {};
    }
    // Flat array: [{ name, price, ... }] — put under "Customise" group
    if (Array.isArray(rawModifiers) && rawModifiers.length > 0) {
      return { 'Customise': rawModifiers };
    }
    return {};
  })();

  // Build ingredients — filter product-level ingredients by variation name
  const variationName = selectedVariation?.name || null;
  const rawIngredients = product.ingredients || null;
  const ingredientList = React.useMemo(() => {
    if (!rawIngredients || !Array.isArray(rawIngredients)) return [];

    // Filter by variant name if ingredients have a "variant" field
    const hasVariantField = rawIngredients.some(i => typeof i === 'object' && i.variant);

    let filtered = rawIngredients;
    if (hasVariantField && variationName) {
      filtered = rawIngredients.filter(i =>
        typeof i === 'object' && i.variant === variationName
      );
    }

    // Sort by sort_order if available
    filtered = [...filtered].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    return filtered
      .map(i => {
        if (typeof i === 'string') {
          return { name: i, outOfStock: false, swappable: false, swapOptions: [] };
        }
        return {
          name: i.name || i.ingredient || '',
          outOfStock: !!i.outOfStock,
          swappable: !!i.swappable,
          swapOptions: Array.isArray(i.swapOptions) ? i.swapOptions : [],
        };
      })
      .filter(i => i.name);
  }, [rawIngredients, variationName]);

  const [removedIngredients, setRemovedIngredients] = useState(() => {
    if (!editMode || !editCartItem?.notes) return new Set();
    const noLines = editCartItem.notes.split('\n').filter(l => l.match(/^NO:\s/i));
    const removed = new Set();
    for (const line of noLines) {
      const match = line.match(/^NO:\s*(.+)$/i);
      if (match) match[1].split(',').map(s => s.trim()).filter(Boolean).forEach(ing => removed.add(ing));
    }
    return removed;
  });
  const [swappedIngredients, setSwappedIngredients] = useState(() => {
    if (!editMode || !editCartItem?.notes) return {};
    const out = {};
    for (const line of editCartItem.notes.split('\n')) {
      const m = line.match(/^SWAP:\s*(.+?)\s*->\s*(.+)$/i);
      if (m) out[m[1].trim()] = m[2].trim();
    }
    return out;
  });
  const [ingredientsExpanded, setIngredientsExpanded] = useState(
    editMode && (removedIngredients.size > 0 || Object.keys(swappedIngredients).length > 0)
  );

  // Reset removed ingredients when variation changes (ingredient list changes) — skip initial render in edit mode
  const [hasChangedVariation, setHasChangedVariation] = useState(false);
  useEffect(() => {
    if (editMode && !hasChangedVariation) {
      setHasChangedVariation(true);
      return;
    }
    setRemovedIngredients(new Set());
    setSwappedIngredients({});
    setIngredientsExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariation]);

  const toggleModifier = (groupName, item) => {
    setSelectedModifiers(prev => {
      const existing = prev[groupName] || [];
      const itemName = typeof item === 'string' ? item : item.name;
      const idx = existing.findIndex(m => m.name === itemName);
      if (idx >= 0) {
        const updated = existing.filter((_, i) => i !== idx);
        return { ...prev, [groupName]: updated };
      }
      return { ...prev, [groupName]: [...existing, typeof item === 'string' ? { name: item, price: 0 } : item] };
    });
  };

  const isModifierSelected = (groupName, item) => {
    const existing = selectedModifiers[groupName] || [];
    const name = typeof item === 'string' ? item : item.name;
    return existing.some(m => m.name === name);
  };

  const handleAddToCart = () => {
    // Flatten modifiers to a plain object: { groupName: [names] }
    const flatModifiers = {};
    for (const [group, items] of Object.entries(selectedModifiers)) {
      if (items.length > 0) {
        flatModifiers[group] = items.map(m => m.name || m);
      }
    }

    // Add removed ingredients (including out-of-stock) to notes
    const allRemoved = new Set(removedIngredients);
    ingredientList.filter(i => i.outOfStock).forEach(i => allRemoved.add(i.name));
    let finalNotes = notes;
    if (allRemoved.size > 0) {
      const removedList = Array.from(allRemoved).join(', ');
      const removeNote = `NO: ${removedList}`;
      finalNotes = notes ? `${notes}\n${removeNote}` : removeNote;
    }

    // Append SWAP: lines for each swapped ingredient (skip ones that were removed)
    const swapLines = Object.entries(swappedIngredients)
      .filter(([from, to]) => from && to && !allRemoved.has(from))
      .map(([from, to]) => `SWAP: ${from} -> ${to}`);
    if (swapLines.length > 0) {
      finalNotes = finalNotes ? `${finalNotes}\n${swapLines.join('\n')}` : swapLines.join('\n');
    }

    if (editMode && onEditComplete) {
      onEditComplete(product, selectedVariation, quantity, flatModifiers, finalNotes, modifierCost);
    } else {
      addItem(product, selectedVariation, quantity, flatModifiers, finalNotes, modifierCost);
    }
    setAdded(true);
    setTimeout(handleClose, 700);
  };

  return (
    <div
      className={`product-detail-overlay${visible ? ' product-detail-overlay--visible' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={product.name}
    >
      <div className={`product-detail${visible ? ' product-detail--visible' : ''}`}>
        {/* Close button */}
        <button className="product-detail__close" onClick={handleClose} aria-label="Close">
          <span className="product-detail__close-icon"><X size={20} /></span>
        </button>

        {/* Scrollable inner content */}
        <div className="product-detail__scroll">
          {/* Hero image */}
          <div className="product-detail__hero">
            {(selectedVariation?.online_image_url || selectedVariation?.image_url || product.online_image_url || product.image_url) ? (
              <img
                src={selectedVariation?.online_image_url || selectedVariation?.image_url || product.online_image_url || product.image_url}
                alt={product.name}
                className="product-detail__hero-img"
              />
            ) : (
              <div className="product-detail__hero-placeholder">
                <span className="product-detail__hero-emoji"><PlaceholderIcon product={product} /></span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="product-detail__body">
            <h2 className="product-detail__name">
              {selectedVariation ? selectedVariation.name : product.name}
            </h2>

            {product.online_description && (
              <p className="product-detail__description">{product.online_description}</p>
            )}

            <p className="product-detail__price">{formatPrice(unitPrice)}</p>

            {/* Modifiers */}
            {Object.entries(modifierGroups).map(([groupName, items]) => (
              <div key={groupName} className="product-detail__section">
                <h3 className="product-detail__section-title">{groupName}</h3>
                <div className="modifier-list">
                  {(Array.isArray(items) ? items : []).map((item, idx) => {
                    const itemName = typeof item === 'string' ? item : item.name;
                    const itemPrice = typeof item === 'object' && item.price ? Number(item.price) : 0;
                    const itemOOS = typeof item === 'object' && !!item.outOfStock;
                    const selected = !itemOOS && isModifierSelected(groupName, item);
                    return (
                      <label key={itemName || idx} className={`modifier-option${selected ? ' modifier-option--selected' : ''}${itemOOS ? ' modifier-option--out-of-stock' : ''}`} style={itemOOS ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={itemOOS}
                          onChange={() => toggleModifier(groupName, item)}
                          className="modifier-option__checkbox"
                        />
                        <span className="modifier-option__name">{itemName}{itemOOS ? ' (Out of Stock)' : ''}</span>
                        {itemPrice > 0 && !itemOOS && (
                          <span className="modifier-option__price">+{formatPrice(itemPrice)}</span>
                        )}
                        <span className={`modifier-option__check${selected ? ' modifier-option__check--selected' : ''}`}>
                          {selected ? <Check size={16} /> : ''}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Ingredients — expandable checklist, all on by default */}
            {ingredientList.length > 0 && (
              <div className="product-detail__section">
                <button
                  type="button"
                  className="ingredients-toggle"
                  onClick={() => setIngredientsExpanded(prev => !prev)}
                >
                  <h3 className="product-detail__section-title" style={{ margin: 0 }}>
                    Customise
                    {removedIngredients.size > 0 && (
                      <span className="ingredients-removed-count">
                        {removedIngredients.size} removed
                      </span>
                    )}
                  </h3>
                  <span className={`ingredients-chevron${ingredientsExpanded ? ' ingredients-chevron--open' : ''}`}>
                    ▸
                  </span>
                </button>
                {ingredientsExpanded && (
                  <div className="ingredients-list">
                    {ingredientList.map(ing => {
                      const isOOS = ing.outOfStock;
                      const isRemoved = isOOS || removedIngredients.has(ing.name);
                      const canSwap = ing.swappable && Array.isArray(ing.swapOptions) && ing.swapOptions.length > 0;

                      if (canSwap && !isOOS) {
                        const activeChoice = swappedIngredients[ing.name] || ing.name;
                        const allChoices = [ing.name, ...ing.swapOptions];
                        return (
                          <div key={ing.name} className="ingredient-swap-row">
                            {allChoices.map(choice => {
                              const isActive = activeChoice === choice && !isRemoved;
                              return (
                                <button
                                  type="button"
                                  key={choice}
                                  className={`ingredient-swap-btn${isActive ? ' ingredient-swap-btn--active' : ''}`}
                                  onClick={() => {
                                    setSwappedIngredients(prev => {
                                      const next = { ...prev };
                                      if (choice === ing.name) delete next[ing.name];
                                      else next[ing.name] = choice;
                                      return next;
                                    });
                                    // Ensure the ingredient stays included when swapping
                                    if (isRemoved) {
                                      setRemovedIngredients(prev => {
                                        const next = new Set(prev);
                                        next.delete(ing.name);
                                        return next;
                                      });
                                    }
                                  }}
                                >
                                  {choice}
                                </button>
                              );
                            })}
                          </div>
                        );
                      }

                      return (
                        <label key={ing.name} className={`ingredient-option${isRemoved ? ' ingredient-option--removed' : ''}${isOOS ? ' ingredient-option--out-of-stock' : ''}`} style={isOOS ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                          <input
                            type="checkbox"
                            checked={!isRemoved}
                            disabled={isOOS}
                            onChange={() => {
                              setRemovedIngredients(prev => {
                                const next = new Set(prev);
                                if (next.has(ing.name)) {
                                  next.delete(ing.name);
                                } else {
                                  next.add(ing.name);
                                }
                                return next;
                              });
                            }}
                            className="ingredient-option__checkbox"
                          />
                          <span className={`ingredient-option__name${isRemoved ? ' ingredient-option__name--struck' : ''}`}>
                            {ing.name}{isOOS ? ' (Out of Stock)' : ''}
                          </span>
                          <span className={`ingredient-option__check${!isRemoved ? ' ingredient-option__check--on' : ''}`}>
                            {!isRemoved ? <Check size={14} /> : <X size={14} />}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Quantity selector */}
            <div className="product-detail__section product-detail__section--row">
              <h3 className="product-detail__section-title">Quantity</h3>
              <div className="quantity-selector">
                <button
                  className="quantity-selector__btn"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span className="quantity-selector__value">{quantity}</span>
                <button
                  className="quantity-selector__btn"
                  onClick={() => setQuantity(q => q + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* Sticky add-to-cart footer */}
        <div className="product-detail__footer">
          <button
            className={`add-to-cart-btn${added ? ' add-to-cart-btn--added' : ''}`}
            onClick={handleAddToCart}
            disabled={added}
          >
            {added ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{editMode ? 'Updated' : 'Added'} <Check size={16} /></span>
            ) : (
              <>
                <span>{editMode ? 'Update item' : 'Add to cart'}</span>
                <span className="add-to-cart-btn__total">{formatPrice(lineTotal)}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
